import React, { useMemo, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { Card, Table, Button, Tag, App, Modal, Input, Space, Typography, Descriptions, Tooltip } from 'antd';
import { ArrowLeftOutlined, CheckOutlined, PrinterOutlined, ShoppingCartOutlined, CheckCircleOutlined, SendOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { bidAnalysisService, type ComparisonOption } from '@/services/bidAnalysis.service';
import { supplierInquiryService } from '@/services/supplierInquiry.service';
import { purchaseOrderService } from '@/services/purchaseOrder.service';
import { PrintableBidAnalysis } from '@/components/bid-analysis';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';
import { projectLabel } from '@/utils/projectDisplay';
import ProcurementAttachmentsCard from '@/components/procurement/ProcurementAttachmentsCard';
import { LineSpecificationsText, isSpecDeviation } from '@/components/procurement/LineSpecifications';
import { DOCUMENT_ENTITY_TYPE_BID_ANALYSIS } from '@/constants/document-entity-types';
import { CreatePOFromBidAnalysisModeModal } from '@/components/procurement/CreatePOFromBidAnalysisModeModal';
import { DocumentRefLink } from '@/components/DocumentRefLink';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';

const BidAnalysisDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [savingLine, setSavingLine] = useState<string | null>(null);
  const [acceptAllLoading, setAcceptAllLoading] = useState(false);
  const [createPOLoading, setCreatePOLoading] = useState(false);
  const [createPOModalOpen, setCreatePOModalOpen] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [justificationModal, setJustificationModal] = useState<{ inquiryLineId: string; quotationLineId: string; option: ComparisonOption } | null>(null);
  const [overrideJustification, setOverrideJustification] = useState('');
  const { data: departments = [] } = useDepartmentsForSelection();

  const departmentLabel = (deptId?: string | null) => {
    if (!deptId) return '—';
    const d = departments.find((x) => x.id === deptId);
    return d ? `${d.department_code} - ${d.department_name}` : deptId;
  };

  const { data: analysis, isLoading: analysisLoading } = useQuery({
    queryKey: ['bid-analysis', id],
    queryFn: () => bidAnalysisService.getById(id!),
    enabled: !!id,
  });

  const { data: inquiry } = useQuery({
    queryKey: ['supplier-inquiry', analysis?.supplier_inquiry_id],
    queryFn: () => supplierInquiryService.getById(analysis!.supplier_inquiry_id),
    enabled: !!analysis?.supplier_inquiry_id,
  });

  const { data: quotations = [] } = useQuery({
    queryKey: ['bid-analysis-quotations', id],
    queryFn: () => bidAnalysisService.getQuotationsForBidAnalysis(id!),
    enabled: !!id,
  });

  const { data: comparison } = useQuery({
    queryKey: ['bid-analysis-comparison', id],
    queryFn: () => bidAnalysisService.getComparisonWithRecommendations(id!),
    enabled: !!id,
  });

  const { data: selections = [], refetch: refetchSelections } = useQuery({
    queryKey: ['bid-analysis-selections', id],
    queryFn: () => bidAnalysisService.getSelections(id!),
    enabled: !!id,
  });

  const getSelectionForInquiryLine = (inquiryLineId: string) =>
    selections.find((s: any) => s.supplier_inquiry_line_id === inquiryLineId);

  const getOptionsFromQuotations = (inquiryLineId: string): ComparisonOption[] => {
    const out: ComparisonOption[] = [];
    quotations.forEach((q: any) => {
      (q.lines || []).forEach((line: any) => {
        if (line.supplier_inquiry_line_id !== inquiryLineId) return;
        const unitPrice = Number(line.unit_price) || 0;
        const qty = Number(line.quoted_quantity) || 0;
        const lineTotal = Number(line.line_total) ?? unitPrice * qty;
        out.push({
          quotationLineId: line.id,
          supplierQuotationId: q.id,
          supplierId: q.supplier_id,
          supplierName: (q.supplier as any)?.supplier_name ?? q.supplier_id,
          unit_price: unitPrice,
          line_total: lineTotal,
          lead_time_days: line.lead_time_days != null ? Number(line.lead_time_days) : undefined,
          promised_delivery_date: line.promised_delivery_date
            ? (typeof line.promised_delivery_date === 'string' ? line.promised_delivery_date : line.promised_delivery_date.slice(0, 10))
            : undefined,
          offered_specifications: line.compliance_notes ?? null,
          brand_offered: line.brand_offered ?? null,
        });
      });
    });
    return out;
  };

  const handleSelectLine = async (inquiryLineId: string, quotationLineId: string, option: ComparisonOption, isRecommended: boolean) => {
    if (!isRecommended) {
      setJustificationModal({ inquiryLineId, quotationLineId, option });
      setOverrideJustification('');
      return;
    }
    await applySelection(inquiryLineId, quotationLineId, option.unit_price, undefined);
  };

  const applySelection = async (inquiryLineId: string, quotationLineId: string, negotiated_unit_price?: number, justification?: string) => {
    setSavingLine(inquiryLineId);
    try {
      await bidAnalysisService.setSelection(id!, {
        supplier_inquiry_line_id: inquiryLineId,
        supplier_quotation_line_id: quotationLineId,
        selected: true,
        negotiated_unit_price,
        justification,
      });
      message.success('Selection saved');
      refetchSelections();
      queryClient.invalidateQueries({ queryKey: ['bid-analysis-selections', id] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to save selection');
    } finally {
      setSavingLine(null);
    }
  };

  const submitOverrideWithJustification = async () => {
    if (!justificationModal) return;
    await applySelection(
      justificationModal.inquiryLineId,
      justificationModal.quotationLineId,
      justificationModal.option.unit_price,
      overrideJustification.trim() || 'Override: non-recommended supplier selected'
    );
    setJustificationModal(null);
    setOverrideJustification('');
  };

  const handlePrint = () => {
    const mainContent = document.getElementById('root');
    if (mainContent) mainContent.style.display = 'none';
    const printContainer = document.createElement('div');
    printContainer.id = 'print-container';
    printContainer.style.display = 'block';
    document.body.appendChild(printContainer);
    const root = ReactDOM.createRoot(printContainer);
    const cleanup = () => {
      root.unmount();
      if (document.body.contains(printContainer)) document.body.removeChild(printContainer);
      if (mainContent) mainContent.style.display = 'block';
      window.removeEventListener('afterprint', cleanup);
    };
    root.render(
      <PrintableBidAnalysis
        analysisNumber={analysis?.analysis_number ?? ''}
        inquiryNumber={analysis?.supplierInquiry?.inquiry_number ?? analysis?.supplier_inquiry_id ?? ''}
        status={analysis?.status ?? ''}
        createdAt={analysis?.created_at}
        recommendationNotes={analysis?.recommendation_notes}
        approvalNotes={analysis?.approval_notes}
        comparison={comparison ?? null}
        selections={selections.map((s: any) => ({
          supplier_inquiry_line_id: s.supplier_inquiry_line_id,
          supplier_quotation_line_id: s.supplier_quotation_line_id,
          justification: s.justification,
        }))}
        quotations={quotations}
        onClose={cleanup}
      />
    );
    window.addEventListener('afterprint', cleanup);
  };

  const handleApprove = async () => {
    if (!id) return;
    setApproveLoading(true);
    try {
      await bidAnalysisService.updateStatus(id, 'Approved');
      message.success('Bid analysis approved');
      queryClient.invalidateQueries({ queryKey: ['bid-analysis', id] });
      queryClient.invalidateQueries({ queryKey: ['bid-analyses'] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to approve');
    } finally {
      setApproveLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!id) return;
    setSubmitLoading(true);
    try {
      await bidAnalysisService.updateStatus(id, 'Submitted');
      message.success('Bid analysis submitted for approval');
      queryClient.invalidateQueries({ queryKey: ['bid-analysis', id] });
      queryClient.invalidateQueries({ queryKey: ['bid-analyses'] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to submit');
    } finally {
      setSubmitLoading(false);
    }
  };

  const hasRemainingConvertible = useMemo(() => {
    return (selections as any[]).some((s) => {
      if (!s.selected) return false;
      const ql = s.supplierQuotationLine ?? s.supplier_quotation_line;
      const quoted = Number(ql?.quoted_quantity ?? 0);
      const converted = Number(s.converted_quantity ?? 0);
      return quoted - converted > 0.0001;
    });
  }, [selections]);

  const handleCreatePurchaseOrders = async () => {
    if (!id) return;
    const lines = comparison?.inquiryLines ?? inquiry?.lines ?? [];
    const selected = selections.filter((s: any) => s.selected);
    const blockers = selected.filter((s: any) => {
      const inqLineId = s.supplier_inquiry_line_id;
      const inqLine =
        s.supplierInquiryLine ??
        s.supplier_inquiry_line ??
        lines.find((l: any) => l.id === inqLineId);
      return !inqLine?.project_id;
    });
    if (blockers.length) {
      message.error(
        `Cannot create Purchase Order: ${blockers.length} selected line(s) have no project. Set project on the PR/inquiry line first.`
      );
      return;
    }
    if (!selected.length) {
      message.warning('Select at least one quotation line before creating a Purchase Order');
      return;
    }
    if (!hasRemainingConvertible) {
      message.warning('All selected lines are already fully converted to Purchase Order(s)');
      return;
    }
    setCreatePOModalOpen(true);
  };

  const createPoSuppliers = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of selections as any[]) {
      if (!s.selected) continue;
      const ql = s.supplierQuotationLine ?? s.supplier_quotation_line;
      const quoted = Number(ql?.quoted_quantity ?? 0);
      const converted = Number(s.converted_quantity ?? 0);
      if (quoted - converted <= 0) continue;
      const sid = s.supplierQuotation?.supplier_id ?? s.supplier_quotation?.supplier_id;
      if (!sid) continue;
      const name =
        s.supplierQuotation?.supplier?.supplier_name ??
        s.supplierQuotation?.supplier_name ??
        sid;
      map.set(sid, name);
    }
    return [...map.entries()].map(([supplierId, supplierName]) => ({ supplierId, supplierName }));
  }, [selections]);

  const createPoCurrency =
    (inquiry as any)?.currency ||
    (analysis as any)?.supplierInquiry?.currency ||
    'AED';

  const { data: linkedPurchaseOrders = [], isLoading: linkedPosLoading } = useQuery({
    queryKey: ['purchase-orders', 'by-bid-analysis', id],
    queryFn: () => purchaseOrderService.list({ bid_analysis_id: id! }),
    enabled: !!id,
    staleTime: 30000,
  });

  const confirmCreatePurchaseOrders = async (payload: {
    mode: 'new' | 'revise_existing';
    supplier_revisions?: Array<{ supplier_id: string; target_po_id: string }>;
    target_po_id?: string;
  }) => {
    if (!id) return;
    setCreatePOLoading(true);
    try {
      const result = await purchaseOrderService.createFromBidAnalysis(id, {
        ...payload,
        department_id: analysis?.department_id || inquiry?.department_id || undefined,
      });
      const created = result?.purchaseOrders ?? [];
      if (created.length === 0) {
        message.warning('No purchase orders were created');
        return;
      }
      setCreatePOModalOpen(false);
      message.success(
        payload.mode === 'revise_existing'
          ? `Updated ${created.length} Purchase Order(s)`
          : `Created ${created.length} Purchase Order(s)`
      );
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-orders', 'by-bid-analysis', id] });
      queryClient.invalidateQueries({ queryKey: ['bid-analysis-selections', id] });
      if (created.length === 1) {
        navigate(`/scm/procurement/purchase-orders/${created[0].id}`);
      } else {
        Modal.success({
          title: 'Purchase Orders ready',
          content: (
            <div>
              <p>{created.length} PO(s). Open from Purchase Orders list or:</p>
              <Space direction="vertical" size="small">
                {created.map((po) => (
                  <Button key={po.id} type="link" size="small" onClick={() => { Modal.destroyAll(); navigate(`/scm/procurement/purchase-orders/${po.id}`); }}>
                    {po.po_number}
                  </Button>
                ))}
              </Space>
            </div>
          ),
        });
      }
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to create Purchase Order(s)');
    } finally {
      setCreatePOLoading(false);
    }
  };

  const acceptAllRecommendations = async () => {
    if (!comparison?.lineComparisons) return;
    setAcceptAllLoading(true);
    let applied = 0;
    try {
      for (const inqLine of comparison.inquiryLines) {
        const lineComp = comparison.lineComparisons[inqLine.id];
        if (!lineComp?.recommendedQuotationLineId) continue;
        const opt = lineComp.options.find((o) => o.quotationLineId === lineComp.recommendedQuotationLineId);
        if (!opt) continue;
        await bidAnalysisService.setSelection(id!, {
          supplier_inquiry_line_id: inqLine.id,
          supplier_quotation_line_id: opt.quotationLineId,
          selected: true,
          negotiated_unit_price: opt.unit_price,
        });
        applied++;
      }
      message.success(`Applied recommendation for ${applied} line(s)`);
      refetchSelections();
      queryClient.invalidateQueries({ queryKey: ['bid-analysis-selections', id] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Failed to apply recommendations');
    } finally {
      setAcceptAllLoading(false);
    }
  };

  if (!id || (analysisLoading && !analysis)) return null;
  if (!analysis) return <Card><p>Bid analysis not found.</p></Card>;

  const inquiryLines = comparison?.inquiryLines ?? inquiry?.lines ?? [];

  const hasRecommendations = comparison?.lineComparisons && Object.values(comparison.lineComparisons).some((lc) => lc.recommendedQuotationLineId);

  return (
    <div style={{ padding: 24 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/scm/procurement/bid-analyses')} style={{ marginBottom: 16 }}>
        Back
      </Button>
      <Card
        title={`Bid analysis ${analysis.analysis_number}`}
        extra={
          <>
            <Tag color="blue">{analysis.status}</Tag>
            <Button type="link" size="small" onClick={() => navigate(`/scm/procurement/supplier-inquiries/${analysis.supplier_inquiry_id}`)}>
              View inquiry
            </Button>
            <Button type="default" icon={<PrinterOutlined />} onClick={handlePrint}>
              Print for approval
            </Button>
            <ReportDocumentActions
              reportKey="bid-analysis"
              params={{ id: analysis.id }}
              documentId={analysis.id}
              sendPermissions={['procurement.rfq.edit', 'procurement.po.edit']}
            />
            {(analysis.status === 'Draft' || analysis.status === 'Submitted') && (
              <>
                {analysis.status === 'Draft' && (
                  <Button type="default" icon={<SendOutlined />} loading={submitLoading} onClick={handleSubmit}>
                    Submit for approval
                  </Button>
                )}
                <Button type="primary" icon={<CheckCircleOutlined />} loading={approveLoading} onClick={handleApprove}>
                  Approve
                </Button>
              </>
            )}
            {analysis.status === 'Approved' && hasRemainingConvertible && (
              <Button type="primary" icon={<ShoppingCartOutlined />} loading={createPOLoading} onClick={handleCreatePurchaseOrders}>
                Create Purchase Order(s)
              </Button>
            )}
            {analysis.status === 'Approved' && !hasRemainingConvertible && selections.some((s: any) => s.selected) && (
              <Tag color="default">Fully converted to PO</Tag>
            )}
          </>
        }
      >
        <Descriptions column={2} size="small" style={{ marginBottom: 12 }}>
          <Descriptions.Item label="Status">{analysis.status}</Descriptions.Item>
          <Descriptions.Item label="Department">
            {analysis.status === 'Draft' ? (
              <DepartmentSelect
                style={{ width: 280 }}
                value={analysis.department_id || inquiry?.department_id || undefined}
                onChange={async (v) => {
                  try {
                    await bidAnalysisService.updateNotes(id!, undefined, undefined, v as string);
                    message.success('Department updated');
                    queryClient.invalidateQueries({ queryKey: ['bid-analysis', id] });
                  } catch (e: any) {
                    message.error(e?.response?.data?.message || 'Failed to update department');
                  }
                }}
              />
            ) : (
              departmentLabel(analysis.department_id || inquiry?.department_id)
            )}
          </Descriptions.Item>
        </Descriptions>
        <p>
          <Typography.Text strong>Document links: </Typography.Text>
          <Space wrap size="small">
            <DocumentRefLink
              to={`/scm/procurement/supplier-inquiries/${analysis.supplier_inquiry_id}`}
              number={analysis.supplierInquiry?.inquiry_number ?? inquiry?.inquiry_number}
              id={analysis.supplier_inquiry_id}
              name="Inquiry"
            />
            {quotations.map((q: any) => (
              <DocumentRefLink
                key={q.id}
                to={`/scm/procurement/supplier-quotations/${q.id}`}
                number={q.quotation_number}
                id={q.id}
              />
            ))}
          </Space>
        </p>
        <p><strong>Quotations received:</strong> {quotations.length}</p>
        <div style={{ marginTop: 8 }}>
          <Typography.Text strong>Purchase Orders created: </Typography.Text>
          {linkedPosLoading ? (
            <span>Loading…</span>
          ) : linkedPurchaseOrders.length === 0 ? (
            <Typography.Text type="secondary">None yet</Typography.Text>
          ) : (
            <Space wrap size="small">
              {linkedPurchaseOrders.map((po: any) => (
                <DocumentRefLink
                  key={po.id}
                  to={`/scm/procurement/purchase-orders/${po.id}`}
                  number={
                    po.revision_number != null
                      ? `${po.po_number} (${po.po_status} · Rev ${po.revision_number})`
                      : `${po.po_number} (${po.po_status})`
                  }
                  id={po.id}
                />
              ))}
            </Space>
          )}
        </div>
      </Card>

      <Card
        title="Compare & select by line"
        extra={
          hasRecommendations ? (
            <Button type="primary" icon={<CheckOutlined />} loading={acceptAllLoading} onClick={acceptAllRecommendations}>
              Accept all recommendations
            </Button>
          ) : null
        }
        style={{ marginTop: 16 }}
      >
        <Table
          size="small"
          rowKey="id"
          dataSource={inquiryLines}
          pagination={false}
          columns={[
            { title: '#', dataIndex: 'line_number', width: 50 },
            { title: 'Item', dataIndex: 'item_code', width: 100 },
            {
              title: 'Description',
              dataIndex: 'item_description',
              render: (desc: string, inqLine: any) => (
                <>
                  <div>{desc}</div>
                  <LineSpecificationsText value={inqLine.specifications} label="Required spec" />
                </>
              ),
            },
            { title: 'Qty', dataIndex: 'quantity', width: 70, align: 'right' },
            { title: 'UOM', dataIndex: 'uom', width: 55 },
            { title: 'Project', key: 'project', width: 120, render: (_: unknown, inqLine: any) => projectLabel(inqLine) },
            {
              title: 'Quotes (supplier / price / total / lead)',
              key: 'quotes',
              render: (_: any, inqLine: any) => {
                const lineComp = comparison?.lineComparisons?.[inqLine.id];
                const options: ComparisonOption[] = lineComp?.options?.length ? lineComp.options : getOptionsFromQuotations(inqLine.id);
                const sel = getSelectionForInquiryLine(inqLine.id);
                const recommendedId = lineComp?.recommendedQuotationLineId ?? null;
                const recommendedReason = lineComp?.recommendedReason ?? '';
                if (options.length === 0) {
                  return <span style={{ color: '#999' }}>No quotes</span>;
                }
                return (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                    {options.map((opt) => {
                      const isRecommended = opt.quotationLineId === recommendedId;
                      const isSelected = sel?.supplier_quotation_line_id === opt.quotationLineId;
                      const lead = opt.lead_time_days != null ? `${opt.lead_time_days}d` : '';
                      const delivery = opt.promised_delivery_date ? dayjs(opt.promised_delivery_date).format('DD MMM YY') : '';
                      return (
                        <Space key={opt.quotationLineId} wrap size="small">
                          <Button
                            type={isSelected ? 'primary' : 'default'}
                            size="small"
                            loading={savingLine === inqLine.id}
                            onClick={() => handleSelectLine(inqLine.id, opt.quotationLineId, opt, isRecommended)}
                          >
                            {opt.supplierName}: {opt.unit_price.toFixed(2)} ({opt.line_total.toFixed(2)}) {lead ? ` · ${lead}` : ''} {delivery ? ` · ${delivery}` : ''}
                          </Button>
                          {isRecommended && (
                            <Tag color="green" title={recommendedReason}>Recommended</Tag>
                          )}
                          {opt.brand_offered ? <Tag>Brand: {opt.brand_offered}</Tag> : null}
                          {opt.uom_mismatch ? (
                            <Tooltip title={`Quoted in ${opt.quoted_uom}; the RFQ asks for ${inqLine.uom ?? '—'}. Prices are not directly comparable.`}>
                              <Tag color="red">UOM {opt.quoted_uom}</Tag>
                            </Tooltip>
                          ) : null}
                          {opt.price_per_kg != null ? (
                            <Tooltip title="Line total ÷ theoretical weight from the Purchase Request (information only)">
                              <Tag>{opt.price_per_kg.toFixed(2)}/kg</Tag>
                            </Tooltip>
                          ) : null}
                          {opt.offered_specifications ? (
                            <Tooltip
                              title={<div style={{ whiteSpace: 'pre-wrap' }}>{opt.offered_specifications}</div>}
                            >
                              {isSpecDeviation(inqLine.specifications, opt.offered_specifications) ? (
                                <Tag color="orange">Spec deviation</Tag>
                              ) : (
                                <Tag color="blue">Offered spec</Tag>
                              )}
                            </Tooltip>
                          ) : null}
                        </Space>
                      );
                    })}
                  </div>
                );
              },
            },
            {
              title: 'Selected',
              width: 140,
              render: (_: any, inqLine: any) => {
                const sel = getSelectionForInquiryLine(inqLine.id);
                if (!sel) return <span style={{ color: '#999' }}>—</span>;
                const q = quotations.find((qu: any) => qu.id === sel.supplier_quotation_id);
                return (q?.supplier as any)?.supplier_name ?? sel.supplier_quotation_id;
              },
            },
          ]}
        />
      </Card>

      <ProcurementAttachmentsCard
        entityType={DOCUMENT_ENTITY_TYPE_BID_ANALYSIS}
        entityId={id}
        queryKeyPrefix={['documents', 'bid-analysis']}
      />

      <Modal
        title="Justification required"
        open={!!justificationModal}
        onCancel={() => { setJustificationModal(null); setOverrideJustification(''); }}
        onOk={submitOverrideWithJustification}
        okText="Save selection"
      >
        <p>You are selecting a supplier that is not the system recommendation. Please provide a justification (e.g. quality, delivery, existing contract).</p>
        <Input.TextArea
          rows={3}
          placeholder="Justification for selecting this supplier..."
          value={overrideJustification}
          onChange={(e) => setOverrideJustification(e.target.value)}
        />
      </Modal>

      <CreatePOFromBidAnalysisModeModal
        open={createPOModalOpen}
        loading={createPOLoading}
        currency={createPoCurrency}
        suppliers={createPoSuppliers}
        onCancel={() => setCreatePOModalOpen(false)}
        onConfirm={(payload) => void confirmCreatePurchaseOrders(payload)}
      />
    </div>
  );
};

export default BidAnalysisDetailPage;
